/*
 This file is part of GNU Taler
 (C) 2022-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import { Fragment, VNode, h } from "preact";
import { HTMLAttributes, useState } from "preact/compat";
import {
  SafeHandlerTemplate,
  useTranslationContext,
} from "../index.browser.js";
import { doAutoFocus } from "./utils.js";

export interface ButtonHandler {
  onClick: (() => Promise<void>) | undefined;
}

interface Props extends HTMLAttributes<HTMLButtonElement> {
  handler: ButtonHandler | undefined;
}

/**
 * This button accept an async function and report a notification
 * on error or success.
 *
 * When the async function is running the inner text will change into
 * a "loading" animation.
 *
 * @deprecated use ButtonBetter
 *
 * @param param0
 * @returns
 */
export function Button({
  handler,
  children,
  disabled,
  onClick: clickEvent,
  ...rest
}: Props): VNode {
  const { i18n } = useTranslationContext();
  const [running, setRunning] = useState(false);
  return (
    <button
      {...rest}
      disabled={disabled || running}
      onClick={(e) => {
        e.preventDefault();
        if (!handler || !handler.onClick) {
          return;
        }
        setRunning(true);
        handler.onClick().finally(() => {
          setRunning(false);
        });
      }}
    >
      {running ? <Wait /> : children}
    </button>
  );
}

type PropsBetter = Omit<
  Omit<HTMLAttributes<HTMLButtonElement>, "type">,
  "onClick"
> & {
  type: "button" | "submit";
  onClick: SafeHandlerTemplate<any, any> | undefined;
  focus?: boolean;
};
/**
 * FIXME: removed deprecated and change for this one
 * @param param0
 * @returns
 */
export function ButtonBetter({
  children,
  focus,
  onClick,
  disabled,
  ...rest
}: PropsBetter): VNode {
  const [running, setRunning] = useState(false);
  return (
    <button
      {...rest}
      disabled={running || !onClick || !onClick.args || disabled}
      ref={focus ? doAutoFocus : undefined}
      onClick={(e) => {
        e.preventDefault();
        if (!onClick || !onClick.args) {
          return;
        }
        setRunning(true);
        onClick.call().finally(() => {
          setRunning(false);
        });
      }}
    >
      {running ? <Wait /> : children}
    </button>
  );
}
/**
 * we should have a button-type and a submit-type
 * submit tpye should not have focus sin the focus in the form
 * submit should only be used on forms and there should be only one
 */
// FIXME: we should stop using bulma css and remove all of this support
export function ButtonBetterBulma({
  children,
  focus,
  disabled,
  onClick,
  ...rest
}: PropsBetter & { "data-tooltip"?: string }): VNode {
  const [running, setRunning] = useState(false);
  if (onClick) {
    {
      const prev = onClick.onStart;
      onClick.onStart = () => {
        setRunning(true);
        prev();
      };
    }
    {
      const prev = onClick.onSuccess;
      onClick.onSuccess = (...args) => {
        setRunning(false);
        return prev(...args);
      };
    }
    {
      const prev = onClick.onFail;
      onClick.onFail = (...args) => {
        setRunning(false);
        return prev(...args);
      };
    }
  }
  return (
    <button
      class="button is-success"
      {...rest}
      ref={focus ? doAutoFocus : undefined}
      disabled={running || !onClick || !onClick.args || disabled}
      onClick={(e) => {
        e.preventDefault();
        if (!onClick || !onClick.args) {
          return;
        }
        onClick.call();
      }}
    >
      {running ? <Spinner /> : children}
    </button>
  );
}

function Spinner(): VNode {
  return (
    <div
      class="columns is-centered is-vcentered"
      style={{
        width: 80,
        height: 40,
      }}
    >
      <div class="lds-ring small">
        <div />
        <div />
        <div />
        <div />
      </div>
    </div>
  );
}

function Wait(): VNode {
  return (
    <Fragment>
      <div role="status">
        <svg
          aria-hidden="true"
          class="w-8 h-8 text-neutral-tertiary animate-spin fill-brand"
          viewBox="0 0 100 101"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
            fill="currentColor"
          />
          <path
            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
            fill="currentFill"
          />
        </svg>
        <span class="sr-only">Loading...</span>
      </div>
    </Fragment>
  );
}
